import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, ExternalLink, AlertTriangle, Hash } from "lucide-react";
import { getEntityTrackerDashboard } from "@/lib/api";
import { LIFECYCLE_STRATEGIES, STRATEGY_BY_VALUE, statusLabel, PRIO_BY_VALUE } from "@/lib/tracker";
import { euro, germanDate, LEGAL_FORMS } from "@/lib/format";

const EntityDetail = () => {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = searchParams.get("tab") || "cockpit";

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setErr(null);
        try {
            const d = await getEntityTrackerDashboard(id);
            setData(d);
        } catch (e) {
            setErr(e.response?.data?.detail || "Daten konnten nicht geladen werden");
        } finally { setLoading(false); }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="p-10 text-sm text-gray-500">Lade Cockpit…</div>;
    if (err) return <div className="p-10 text-sm text-red-700">{err}</div>;
    if (!data) return null;
    const { entity, totals, strategies, open_tasks } = data;

    const legalLabel = LEGAL_FORMS.find((l) => l.value === entity.rechtsform)?.label || entity.rechtsform;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <Link to="/entitaeten" className="inline-flex items-center text-xs text-gray-500 hover:text-slate-900 mb-2">
                    <ArrowLeft size={12} className="mr-1" /> zurück zu allen Entitäten
                </Link>
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500">{legalLabel || "Entität"}</div>
                        <h1 className="text-3xl font-heading font-semibold text-slate-900 mt-0.5">{entity.name}</h1>
                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-3">
                            {entity.steuernummer && <span className="inline-flex items-center gap-1"><Hash size={11} />{entity.steuernummer}</span>}
                            <span>{totals.properties} Immobilien · {totals.units} Einheiten · {totals.open_tasks} offene Aufgaben</span>
                        </div>
                    </div>
                </div>
            </div>

            <Tabs value={tab} onValueChange={(v) => setSearchParams(v === "cockpit" ? {} : { tab: v })}>
                <TabsList>
                    <TabsTrigger value="cockpit" data-testid="tab-cockpit">Status-Cockpit</TabsTrigger>
                    <TabsTrigger value="stammdaten" data-testid="tab-stammdaten">Stammdaten</TabsTrigger>
                </TabsList>

                <TabsContent value="cockpit" className="mt-6">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                        {/* Strategie-Kacheln */}
                        <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                            {LIFECYCLE_STRATEGIES.map((strat) => (
                                <StrategyTile
                                    key={strat.value}
                                    strategy={strat}
                                    data={strategies[strat.value]}
                                    entityId={entity.id}
                                />
                            ))}
                        </div>

                        {/* Offene Aufgaben */}
                        <div className="lg:col-span-1 bg-white border border-gray-200 rounded-md overflow-hidden">
                            <div className="px-4 py-3 border-b border-gray-200">
                                <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Offene Aufgaben</div>
                                <div className="flex items-baseline justify-between mt-1">
                                    <div className="text-2xl font-heading font-semibold text-slate-900 tabular-nums">{open_tasks.length}</div>
                                    {open_tasks.filter((t) => t.overdue).length > 0 && (
                                        <div className="text-xs text-red-700 font-medium flex items-center gap-1">
                                            <AlertTriangle size={12} />
                                            {open_tasks.filter((t) => t.overdue).length} überfällig
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="max-h-[420px] overflow-y-auto divide-y divide-gray-100">
                                {open_tasks.length === 0 && (
                                    <div className="px-4 py-10 text-center text-xs text-gray-400">Keine offenen Aufgaben.</div>
                                )}
                                {open_tasks.map((t) => {
                                    const prio = PRIO_BY_VALUE[t.priority] || PRIO_BY_VALUE.normal;
                                    return (
                                        <div key={t.task_id} className={`px-3 py-2 text-xs ${t.overdue ? "bg-red-50" : ""}`} data-testid={`dashboard-task-${t.task_id}`}>
                                            <div className="flex items-start gap-1.5">
                                                <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${prio.dot}`} />
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium text-slate-900 truncate">{t.title}</div>
                                                    <div className="text-[10px] text-gray-500 flex items-center gap-1.5 flex-wrap mt-0.5">
                                                        <Link to={`/einheiten?entity_id=${entity.id}`} className="hover:text-slate-900 underline-offset-2 hover:underline">
                                                            {t.property_label} · {t.unit_label}
                                                        </Link>
                                                        {t.assigned_to && <span>· {t.assigned_to}</span>}
                                                        {t.due_date && <span className={t.overdue ? "text-red-700 font-medium" : ""}>· fällig {germanDate(t.due_date)}</span>}
                                                        {t.link && (
                                                            <a href={t.link} target="_blank" rel="noopener noreferrer" className="text-sky-700 inline-flex items-center gap-0.5 hover:underline">
                                                                <ExternalLink size={9} /> Link
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="stammdaten" className="mt-6">
                    <div className="bg-white border border-gray-200 rounded-md p-5 max-w-2xl text-sm">
                        <dl className="grid grid-cols-3 gap-y-3">
                            <dt className="text-gray-500">Name</dt><dd className="col-span-2 text-slate-900">{entity.name}</dd>
                            <dt className="text-gray-500">Rechtsform</dt><dd className="col-span-2">{legalLabel || "—"}</dd>
                            <dt className="text-gray-500">Steuernummer</dt><dd className="col-span-2">{entity.steuernummer || "—"}</dd>
                            <dt className="text-gray-500">IBAN</dt><dd className="col-span-2 font-mono text-xs">{entity.iban || "—"}</dd>
                            <dt className="text-gray-500">Kontakt</dt><dd className="col-span-2">{entity.kontakt_name || "—"}{entity.kontakt_email ? ` · ${entity.kontakt_email}` : ""}</dd>
                            <dt className="text-gray-500">Notizen</dt><dd className="col-span-2 text-gray-600 whitespace-pre-wrap">{entity.notes || "—"}</dd>
                        </dl>
                        <div className="mt-5 pt-4 border-t border-gray-100">
                            <Link to="/entitaeten">
                                <Button variant="outline" size="sm">In Übersicht bearbeiten</Button>
                            </Link>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

const StrategyTile = ({ strategy, data, entityId }) => {
    const s = strategy;
    const byStatus = Object.entries(data?.by_status || {}).sort((a, b) => b[1] - a[1]);
    const exitYears = (data?.exit_dates || []).map((d) => (d || "").slice(0, 4)).filter(Boolean);
    return (
        <div className={`${s.bg} ${s.border} border rounded-md overflow-hidden`} data-testid={`strategy-tile-${s.value}`}>
            <div className="px-4 py-3 border-b border-current/10 flex items-start justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                        <span className={`font-heading font-semibold ${s.text}`}>{s.label}</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-wider opacity-60 mt-0.5">{s.short}</div>
                </div>
                <div className={`text-3xl font-heading font-bold ${s.text} tabular-nums`}>{data?.count || 0}</div>
            </div>
            <div className="p-4 space-y-3">
                {s.value === "HOLD" && (
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">Ist-Miete (aktive Mieter)</div>
                        <div className={`text-lg font-semibold ${s.text} tabular-nums`}>{euro(data?.ist_miete_monat)} / Mo.</div>
                    </div>
                )}
                {s.value === "TRADE" && (
                    <div className="text-[11px] text-gray-600">Aktive Handelsobjekte. Klicken zum Filtern.</div>
                )}
                {s.value === "HOLD_THEN_SELL" && (
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">Ist-Miete bis Verkauf</div>
                        <div className={`text-lg font-semibold ${s.text} tabular-nums`}>{euro(data?.ist_miete_monat)} / Mo.</div>
                        {exitYears.length > 0 && (
                            <div className="text-[10px] text-gray-500 mt-1">
                                Exit-Jahre: {[...new Set(exitYears)].sort().join(", ")}
                            </div>
                        )}
                    </div>
                )}
                <div className="space-y-1 max-h-32 overflow-y-auto">
                    {byStatus.length === 0 && (
                        <div className="text-[11px] text-gray-400">Keine Einheiten in dieser Strategie.</div>
                    )}
                    {byStatus.map(([st, count]) => (
                        <Link
                            key={st}
                            to={`/einheiten?entity_id=${entityId}&lifecycle_strategy=${s.value}&lifecycle_status=${st}`}
                            className="flex items-center justify-between text-xs hover:bg-white/60 rounded px-1.5 py-1"
                            data-testid={`tile-status-${s.value}-${st}`}
                        >
                            <span className="text-slate-700">{statusLabel(st)}</span>
                            <span className="tabular-nums text-slate-500 font-medium">{count}</span>
                        </Link>
                    ))}
                </div>
                {data?.count > 0 && (
                    <Link
                        to={`/einheiten?entity_id=${entityId}&lifecycle_strategy=${s.value}`}
                        className={`text-[11px] ${s.text} hover:underline inline-flex items-center gap-1`}
                    >
                        Alle Einheiten ({data.count}) ansehen →
                    </Link>
                )}
            </div>
        </div>
    );
};

export default EntityDetail;
